import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardHeader,
  CardContent,
  Alert,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Visibility, VisibilityOff, Check, Close } from '@mui/icons-material';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';

const ChangePasswordPage = () => {
  const { updateToken } = useUser();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  // Password visibility toggles
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Form validation
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Password criteria
  const passwordCriteria = [
    { 
      id: 'length', 
      label: 'לפחות 8 תווים',
      test: (password) => password.length >= 8 
    },
    { 
      id: 'letter', 
      label: 'לפחות אות אחת',
      test: (password) => /[a-zA-Z]/.test(password)
    },
    { 
      id: 'number', 
      label: 'לפחות מספר אחד',
      test: (password) => /[0-9]/.test(password) 
    }
  ];

  // Check password against criteria
  const checkPassword = (password) => {
    return {
      isValid: passwordCriteria.every(criteria => criteria.test(password)),
      criteria: passwordCriteria.map(criteria => ({
        ...criteria,
        valid: criteria.test(password)
      }))
    };
  };

  // Check password strength whenever it changes
  const passwordCheck = checkPassword(newPassword);

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };

    if (!currentPassword) {
      newErrors.currentPassword = 'נדרש להזין סיסמה נוכחית';
      isValid = false;
    }

    if (!newPassword) {
      newErrors.newPassword = 'נדרש להזין סיסמה חדשה';
      isValid = false;
    } else if (!passwordCheck.isValid) {
      newErrors.newPassword = 'הסיסמה אינה עומדת בדרישות';
      isValid = false;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'נדרש לאשר את הסיסמה החדשה';
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'הסיסמאות אינן תואמות';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await API.post('/auth/change-password/', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      
      setSuccessMsg('הסיסמה שונתה בהצלחה!');
      
      // Update tokens if returned from API
      if (response.data.access && response.data.refresh) {
        updateToken(response.data.access, response.data.refresh);
      }
      
      // Clear the form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Stay on the same page (no redirect)
      
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setErrorMsg('הסיסמה הנוכחית שגויה');
      } else {
        setErrorMsg(error.response?.data?.error || 'אירעה שגיאה בעת שינוי הסיסמה');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir="rtl">
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          שינוי סיסמה
        </Typography>
        
        <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
          <CardHeader
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              py: 1.5,
            }}
            title={
              <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                עדכון סיסמה
              </Typography>
            }
          />
          
          <CardContent>
            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMsg}
              </Alert>
            )}
            
            {successMsg && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMsg}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="סיסמה נוכחית"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
                sx={{ direction: 'ltr', textAlign: 'left' }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                label="סיסמה חדשה"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                error={!!errors.newPassword}
                helperText={errors.newPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
                sx={{ direction: 'ltr', textAlign: 'left' }}
              />
              
              {/* Password Requirements List */}
              <List dense sx={{ bgcolor: 'background.paper', my: 1, py: 0 }}>
                {passwordCheck.criteria.map((criteria) => (
                  <ListItem key={criteria.id} sx={{ p: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {criteria.valid ? (
                        <Check sx={{ color: 'success.main' }} />
                      ) : (
                        <Close sx={{ color: 'error.main' }} />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={criteria.label} />
                  </ListItem>
                ))}
              </List>
              
              <TextField
                margin="normal"
                required
                fullWidth
                label="אימות סיסמה חדשה"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
                sx={{ direction: 'ltr', textAlign: 'left' }}
              />
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? 'מעדכן...' : 'עדכן סיסמה'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
};

export default ChangePasswordPage;